import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  PhotoIcon,
  TagIcon,
  ClockIcon,
  ChartBarIcon,
  UserCircleIcon,
  LightBulbIcon,
  SparklesIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Card, CardBody, CardHeader } from '@/components/common/Card';
import Button from '@/components/common/Button';
import Badge from '@/components/common/Badge';
import Modal, { ModalFooter } from '@/components/common/Modal';
import toast from 'react-hot-toast';
import {
  getBlogPost,
  updateBlogPost,
  createBlogPost,
  submitForReview,
  approveBlogPost,
  rejectBlogPost,
  getBlogCategories,
  getBlogAuthors,
  getBlogAuditLog,
  regenerateBlogImage,
  setAllowPublishWithoutImage,
} from '@/services/blogApi';
import { supabase } from '@/services/supabase';
import type { BlogPost, BlogCategory, BlogAuthor, BlogAuditLog, BlogStatus } from '@/types/blog';
import { formatDate } from '@/utils/formatters';
import { validateBlogSeoQa } from '@/utils/blogSeoQa';

const STATUS_BADGES: Record<BlogStatus, { variant: 'gray' | 'info' | 'success' | 'warning' | 'danger'; label: string }> = {
  draft: { variant: 'gray', label: 'Draft' },
  in_review: { variant: 'warning', label: 'In Review' },
  published: { variant: 'success', label: 'Published' },
  archived: { variant: 'info', label: 'Archived' },
};

function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 100);
}

export default function BlogPostDetail() {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = postId === 'new';

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: [] as string[],
    category_slug: '',
    subcategory: '',
    primary_keyword: '',
    tags: [] as string[],
    featured_image_url: '',
    featured_image_alt: '',
    featured_image_suggestion: '', // AI-generated image prompt
    og_title: '',
    og_description: '',
    og_image_url: '',
    canonical_url: '',
    answer_capsule: '',
    blog_author_id: '',
    allow_publish_without_image: false,
  });

  // Image regeneration state
  const [isRegeneratingImage, setIsRegeneratingImage] = useState(false);

  const [keywordInput, setKeywordInput] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [activeTab, setActiveTab] = useState<'content' | 'seo' | 'social' | 'history'>('content');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showOverrideConfirmModal, setShowOverrideConfirmModal] = useState(false);

  // Fetch post
  const { data: post, isLoading } = useQuery<BlogPost | null>({
    queryKey: ['blogPost', postId],
    queryFn: () => (isNew ? Promise.resolve(null) : getBlogPost(postId!)),
    enabled: !isNew,
  });

  // Fetch categories
  const { data: categories } = useQuery<BlogCategory[]>({
    queryKey: ['blogCategories'],
    queryFn: getBlogCategories,
  });

  // Fetch authors
  const { data: authors } = useQuery<BlogAuthor[]>({
    queryKey: ['blogAuthors'],
    queryFn: getBlogAuthors,
  });

  // Fetch audit log
  const { data: auditLog } = useQuery<BlogAuditLog[]>({
    queryKey: ['blogAuditLog', postId],
    queryFn: () => getBlogAuditLog(postId!),
    enabled: !isNew && activeTab === 'history',
  });

  // Populate form when post loads
  useEffect(() => {
    if (post) {
      setFormData({
        title: post.title || '',
        slug: post.slug || '',
        content: post.content || '',
        excerpt: post.excerpt || '',
        meta_title: post.meta_title || '',
        meta_description: post.meta_description || '',
        meta_keywords: post.meta_keywords || [],
        category_slug: post.category_slug || '',
        subcategory: post.subcategory || '',
        primary_keyword: post.primary_keyword || '',
        tags: post.tags || [],
        featured_image_url: post.featured_image_url || '',
        featured_image_alt: post.featured_image_alt || '',
        featured_image_suggestion: post.featured_image_suggestion || '',
        og_title: post.og_title || '',
        og_description: post.og_description || '',
        og_image_url: post.og_image_url || '',
        canonical_url: post.canonical_url || '',
        answer_capsule: post.answer_capsule || '',
        blog_author_id: post.blog_author_id || '',
        allow_publish_without_image: post.allow_publish_without_image || false,
      });
    }
  }, [post]);

  // Track unsaved changes
  useEffect(() => {
    if (post) {
      const hasChanges =
        formData.title !== post.title ||
        formData.content !== post.content ||
        formData.meta_description !== post.meta_description;
      setHasUnsavedChanges(hasChanges);
    } else if (isNew) {
      setHasUnsavedChanges(formData.title.length > 0 || formData.content.length > 0);
    }
  }, [formData, post, isNew]);

  // Auto-generate slug from title
  const handleTitleChange = (title: string) => {
    setFormData((prev) => ({
      ...prev,
      title,
      slug: isNew || !prev.slug ? generateSlug(title) : prev.slug,
    }));
  };

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      const categoryId = categories?.find((c) => c.slug === formData.category_slug)?.id;

      const postData = {
        ...formData,
        canonical_url:
          formData.canonical_url?.trim() ||
          (formData.slug ? `https://linkedbysix.com/blog/${formData.slug}` : ''),
        category_id: categoryId,
        meta_keywords: formData.meta_keywords.length > 0 ? formData.meta_keywords : null,
        tags: formData.tags.length > 0 ? formData.tags : null,
        subcategory: formData.subcategory || null,
        primary_keyword: formData.primary_keyword || null,
        answer_capsule: formData.answer_capsule || null,
        blog_author_id: formData.blog_author_id || null,
        featured_image_suggestion: formData.featured_image_suggestion || null,
      };

      if (isNew) {
        // Get current admin user
        const { data: session } = await supabase.auth.getSession();
        const { data: adminUser } = await supabase
          .from('admin_users')
          .select('id')
          .eq('user_id', session.session?.user?.id)
          .single();

        return createBlogPost({
          ...postData,
          author_id: adminUser?.id,
          status: 'draft',
        });
      } else {
        return updateBlogPost(postId!, postData);
      }
    },
    onSuccess: (data) => {
      toast.success(isNew ? 'Post created' : 'Post saved');
      queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
      queryClient.invalidateQueries({ queryKey: ['blogPost', postId] });
      setHasUnsavedChanges(false);
      if (isNew && data?.id) {
        navigate(`/blog/${data.id}`, { replace: true });
      }
    },
    onError: () => toast.error('Failed to save post'),
  });

  // Submit for review
  const submitMutation = useMutation({
    mutationFn: async () => {
      await saveMutation.mutateAsync();
      return submitForReview(postId!);
    },
    onSuccess: () => {
      toast.success('Submitted for review');
      queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
      queryClient.invalidateQueries({ queryKey: ['blogPost', postId] });
    },
    onError: () => toast.error('Failed to submit for review'),
  });

  // Approve
  const approveMutation = useMutation({
    mutationFn: () => approveBlogPost(postId!),
    onSuccess: () => {
      toast.success('Post published');
      queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
      queryClient.invalidateQueries({ queryKey: ['blogPost', postId] });
    },
    onError: (error: unknown) =>
      toast.error(error instanceof Error ? error.message : 'Failed to publish post'),
  });

  // Reject
  const rejectMutation = useMutation({
    mutationFn: (reason: string) => rejectBlogPost(postId!, reason),
    onSuccess: () => {
      toast.success('Post rejected');
      queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
      queryClient.invalidateQueries({ queryKey: ['blogPost', postId] });
      setShowRejectModal(false);
      setRejectionReason('');
    },
    onError: () => toast.error('Failed to reject post'),
  });

  // Add keyword
  const addKeyword = () => {
    if (keywordInput.trim() && !formData.meta_keywords.includes(keywordInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        meta_keywords: [...prev.meta_keywords, keywordInput.trim()],
      }));
      setKeywordInput('');
    }
  };

  // Add tag
  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  // Publish checks: image requirement + SEO checklist.
  const hasImage = !!formData.featured_image_url?.trim();
  const hasAlt = !!formData.featured_image_alt?.trim();
  const publishQa = validateBlogSeoQa({
    ...formData,
    structured_data: post?.structured_data || null,
    content_json: post?.content_json || null,
  });
  const publishBlockers = publishQa.issues;
  const canPublish = publishBlockers.length === 0;
  const imageMissing = !hasImage || !hasAlt;

  // Handle override toggle with confirmation
  const handleOverrideToggle = async (checked: boolean) => {
    if (checked) {
      // Show confirmation modal before enabling
      setShowOverrideConfirmModal(true);
    } else {
      // Can disable without confirmation
      setFormData((prev) => ({ ...prev, allow_publish_without_image: false }));
      if (postId && !isNew) {
        try {
          await setAllowPublishWithoutImage(postId, false);
          queryClient.invalidateQueries({ queryKey: ['blogPost', postId] });
        } catch {
          toast.error('Failed to update override setting');
        }
      }
    }
  };

  // Confirm override enable
  const confirmOverrideEnable = async () => {
    setFormData((prev) => ({ ...prev, allow_publish_without_image: true }));
    setShowOverrideConfirmModal(false);
    if (postId && !isNew) {
      try {
        await setAllowPublishWithoutImage(postId, true);
        queryClient.invalidateQueries({ queryKey: ['blogPost', postId] });
        toast.success('Override enabled - post can now be published without image');
      } catch {
        toast.error('Failed to update override setting');
        setFormData((prev) => ({ ...prev, allow_publish_without_image: false }));
      }
    }
  };

  // Regenerate featured image using AI
  const handleRegenerateImage = async () => {
    if (!postId || isNew) {
      toast.error('Please save the post first before generating an image');
      return;
    }

    const imagePrompt = formData.featured_image_suggestion?.trim();
    if (!imagePrompt) {
      toast.error('No image prompt available. Please add an image suggestion first.');
      return;
    }

    setIsRegeneratingImage(true);
    try {
      const result = await regenerateBlogImage(
        postId,
        imagePrompt,
        formData.featured_image_alt || `Featured image for ${formData.title}`
      );

      if (result.success && result.image_url) {
        // Update form data with new image
        setFormData((prev) => ({
          ...prev,
          featured_image_url: result.image_url!,
          og_image_url: result.image_url!, // Also update social image
        }));
        toast.success('Image generated successfully!');

        // Mark as having unsaved changes so user saves
        setHasUnsavedChanges(true);
      } else {
        toast.error(result.message || 'Failed to generate image');
      }
    } catch (error) {
      console.error('Image regeneration failed:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to generate image');
    } finally {
      setIsRegeneratingImage(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/blog')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isNew ? 'New Blog Post' : 'Edit Blog Post'}
            </h1>
            {post && (
              <div className="flex items-center gap-2 mt-1">
                <Badge variant={STATUS_BADGES[post.status].variant} dot>
                  {STATUS_BADGES[post.status].label}
                </Badge>
                {post.published_at && (
                  <span className="text-sm text-gray-500">
                    Published {formatDate(post.published_at)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {hasUnsavedChanges && (
            <span className="text-sm text-orange-600">Unsaved changes</span>
          )}
          <Button
            variant="secondary"
            onClick={() => setShowPreview(true)}
            leftIcon={<EyeIcon className="w-4 h-4" />}
          >
            Preview
          </Button>
          <Button
            variant="secondary"
            onClick={() => saveMutation.mutate()}
            loading={saveMutation.isPending}
          >
            Save Draft
          </Button>
          {post?.status === 'draft' && (
            <Button
              onClick={() => submitMutation.mutate()}
              loading={submitMutation.isPending}
            >
              Submit for Review
            </Button>
          )}
          {post?.status === 'in_review' && (
            <>
              <div className="relative group">
                <Button
                  onClick={() => approveMutation.mutate()}
                  loading={approveMutation.isPending}
                  disabled={!canPublish}
                  leftIcon={<CheckCircleIcon className="w-4 h-4" />}
                >
                  Publish
                </Button>
                {!canPublish && (
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                    {publishBlockers[0]?.message || 'Complete SEO checklist requirements to publish'}
                  </div>
                )}
              </div>
              <Button
                variant="danger"
                onClick={() => setShowRejectModal(true)}
                leftIcon={<XCircleIcon className="w-4 h-4" />}
              >
                Reject
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Rejection reason display */}
      {post?.rejection_reason && post.status === 'draft' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h4 className="font-medium text-red-800 mb-1">Rejection Feedback</h4>
          <p className="text-sm text-red-700">{post.rejection_reason}</p>
        </div>
      )}

      {/* Image requirement warning banner */}
      {!isNew && imageMissing && post?.status === 'in_review' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-amber-800 mb-1">
                {!hasImage ? 'Featured Image Required' : 'Alt Text Required'}
              </h4>
              <p className="text-sm text-amber-700 mb-3">
                {!hasImage
                  ? 'This post cannot be published without a featured image. Generate an AI image or upload one manually.'
                  : 'This post cannot be published without alt text for the featured image. Add alt text for accessibility and SEO.'}
              </p>
              <div className="flex items-center gap-3">
                <label htmlFor="allow-publish-without-image" className="flex items-center gap-2 text-sm">
                  <input
                    id="allow-publish-without-image"
                    name="allowPublishWithoutImage"
                    type="checkbox"
                    checked={formData.allow_publish_without_image}
                    onChange={(e) => handleOverrideToggle(e.target.checked)}
                    className="rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                    aria-label="Publish without image as last resort"
                  />
                  <span className="text-amber-800">
                    Publish without image (last resort only)
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {!isNew && post?.status === 'in_review' && publishBlockers.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h4 className="font-medium text-red-800 mb-2">SEO publish checklist incomplete</h4>
          <ul className="list-disc pl-5 space-y-1 text-sm text-red-700">
            {publishBlockers.map((issue, index) => (
              <li key={`${issue.code}-${index}`}>{issue.message}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex gap-6">
              {[
                { id: 'content', label: 'Content', icon: DocumentTextIcon },
                { id: 'seo', label: 'SEO', icon: GlobeAltIcon },
                { id: 'social', label: 'Social', icon: PhotoIcon },
                { id: 'history', label: 'History', icon: ClockIcon },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as typeof activeTab)}
                  className={`flex items-center gap-2 pb-3 border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-brand text-brand'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content Tab */}
          {activeTab === 'content' && (
            <Card>
              <CardBody className="space-y-4">
                <div>
                  <label htmlFor="post-title" className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    id="post-title"
                    name="title"
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                    placeholder="Enter post title..."
                    aria-label="Post title"
                  />
                </div>

                <div>
                  <label htmlFor="post-slug" className="block text-sm font-medium text-gray-700 mb-1">
                    URL Slug
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">/blog/</span>
                    <input
                      id="post-slug"
                      name="slug"
                      type="text"
                      value={formData.slug}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, slug: e.target.value }))
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                      aria-label="URL slug"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="post-content" className="block text-sm font-medium text-gray-700 mb-1">
                    Content *
                  </label>
                  <textarea
                    id="post-content"
                    name="content"
                    value={formData.content}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, content: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand font-mono text-sm"
                    rows={20}
                    placeholder="<article>&#10;  <p>Your content here...</p>&#10;</article>"
                    aria-label="Post content"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Use semantic HTML: &lt;article&gt;, &lt;section&gt;, &lt;h2&gt;,
                    &lt;p&gt;, &lt;ul&gt;, etc.
                  </p>
                </div>

                <div>
                  <label htmlFor="post-excerpt" className="block text-sm font-medium text-gray-700 mb-1">
                    Excerpt
                  </label>
                  <textarea
                    id="post-excerpt"
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, excerpt: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                    rows={3}
                    placeholder="Brief summary for listings (auto-generated if empty)"
                    aria-label="Excerpt"
                  />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <label htmlFor="post-answer-capsule" className="flex items-center gap-2 text-sm font-medium text-blue-900 mb-2">
                    <LightBulbIcon className="w-4 h-4" />
                    Answer Capsule (AI Overview Optimization)
                  </label>
                  <textarea
                    id="post-answer-capsule"
                    name="answerCapsule"
                    value={formData.answer_capsule}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, answer_capsule: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    rows={4}
                    placeholder="40-70 word direct answer optimized for AI Overview and featured snippets..."
                    aria-label="Answer capsule"
                  />
                  <p className="mt-1 text-xs text-blue-700">
                    {formData.answer_capsule.split(/\s+/).filter(Boolean).length} words (target: 40-70)
                  </p>
                </div>
              </CardBody>
            </Card>
          )}

          {/* SEO Tab */}
          {activeTab === 'seo' && (
            <Card>
              <CardBody className="space-y-4">
                <div>
                  <label htmlFor="post-meta-title" className="block text-sm font-medium text-gray-700 mb-1">
                    Meta Title
                  </label>
                  <input
                    id="post-meta-title"
                    name="metaTitle"
                    type="text"
                    value={formData.meta_title}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, meta_title: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                    placeholder="Custom title for search results (defaults to title)"
                    maxLength={60}
                    aria-label="Meta title"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {formData.meta_title.length}/60 characters
                  </p>
                </div>

                <div>
                  <label htmlFor="post-meta-description" className="block text-sm font-medium text-gray-700 mb-1">
                    Meta Description *
                  </label>
                  <textarea
                    id="post-meta-description"
                    name="metaDescription"
                    value={formData.meta_description}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        meta_description: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                    rows={3}
                    placeholder="Compelling description for search results..."
                    maxLength={160}
                    aria-label="Meta description"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {formData.meta_description.length}/160 characters
                  </p>
                </div>

                <div>
                  <label htmlFor="post-primary-keyword" className="block text-sm font-medium text-gray-700 mb-1">
                    Primary Keyword
                  </label>
                  <input
                    id="post-primary-keyword"
                    name="primaryKeyword"
                    type="text"
                    value={formData.primary_keyword}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, primary_keyword: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                    placeholder="Main target keyword for this post"
                    aria-label="Primary keyword"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    The primary search term this post targets
                  </p>
                </div>

                <div>
                  <label htmlFor="post-secondary-keyword" className="block text-sm font-medium text-gray-700 mb-1">
                    Secondary Keywords
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      id="post-secondary-keyword"
                      name="secondaryKeyword"
                      type="text"
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                      placeholder="Add keyword..."
                      aria-label="Secondary keyword"
                    />
                    <Button variant="secondary" onClick={addKeyword}>
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.meta_keywords.map((keyword) => (
                      <span
                        key={keyword}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-sm"
                      >
                        {keyword}
                        <button
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              meta_keywords: prev.meta_keywords.filter(
                                (k) => k !== keyword
                              ),
                            }))
                          }
                          className="text-gray-400 hover:text-gray-600"
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="post-canonical-url" className="block text-sm font-medium text-gray-700 mb-1">
                    Canonical URL
                  </label>
                  <input
                    id="post-canonical-url"
                    name="canonicalUrl"
                    type="url"
                    value={formData.canonical_url}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, canonical_url: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                    placeholder="https://... (only if republishing content)"
                    aria-label="Canonical URL"
                  />
                </div>
              </CardBody>
            </Card>
          )}

          {/* Social Tab */}
          {activeTab === 'social' && (
            <Card>
              <CardBody className="space-y-4">
                <div>
                  <label htmlFor="post-og-title" className="block text-sm font-medium text-gray-700 mb-1">
                    Open Graph Title
                  </label>
                  <input
                    id="post-og-title"
                    name="ogTitle"
                    type="text"
                    value={formData.og_title}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, og_title: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                    placeholder="Title for social sharing (defaults to title)"
                    aria-label="Open Graph title"
                  />
                </div>

                <div>
                  <label htmlFor="post-og-description" className="block text-sm font-medium text-gray-700 mb-1">
                    Open Graph Description
                  </label>
                  <textarea
                    id="post-og-description"
                    name="ogDescription"
                    value={formData.og_description}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, og_description: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                    rows={3}
                    placeholder="Description for social sharing"
                    aria-label="Open Graph description"
                  />
                </div>

                <div>
                  <label htmlFor="post-og-image-url" className="block text-sm font-medium text-gray-700 mb-1">
                    Social Image URL
                  </label>
                  <input
                    id="post-og-image-url"
                    name="ogImageUrl"
                    type="url"
                    value={formData.og_image_url}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, og_image_url: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                    placeholder="https://... (1200x630 recommended)"
                    aria-label="Social image URL"
                  />
                  {formData.og_image_url && (
                    <img
                      src={formData.og_image_url}
                      alt="Social preview"
                      className="mt-2 h-32 object-cover rounded"
                    />
                  )}
                </div>
              </CardBody>
            </Card>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <Card>
              <CardBody>
                {auditLog && auditLog.length > 0 ? (
                  <div className="space-y-4">
                    {auditLog.map((log) => (
                      <div
                        key={log.id}
                        className="flex items-start gap-4 pb-4 border-b border-gray-100 last:border-0"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 capitalize">
                            {log.action.replace(/_/g, ' ')}
                          </p>
                          {log.notes && (
                            <p className="text-sm text-gray-600 mt-1">{log.notes}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            {formatDate(log.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No history yet</p>
                )}
              </CardBody>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Category & Tags */}
          <Card>
            <CardHeader>
              <h3 className="font-medium flex items-center gap-2">
                <TagIcon className="w-4 h-4" />
                Categorization
              </h3>
            </CardHeader>
            <CardBody className="space-y-4">
              <div>
                <label htmlFor="post-category" className="block text-sm font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <select
                  id="post-category"
                  name="category"
                  value={formData.category_slug}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, category_slug: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                  aria-label="Category"
                >
                  <option value="">Select category...</option>
                  {categories?.map((cat) => (
                    <option key={cat.id} value={cat.slug}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="post-subcategory" className="block text-sm font-medium text-gray-700 mb-1">
                  Subcategory
                </label>
                <input
                  id="post-subcategory"
                  name="subcategory"
                  type="text"
                  value={formData.subcategory}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, subcategory: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand text-sm"
                  placeholder="e.g., automotive, home-services"
                  aria-label="Subcategory"
                />
              </div>

              <div>
                <label htmlFor="post-new-tag" className="block text-sm font-medium text-gray-700 mb-1">
                  Tags
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    id="post-new-tag"
                    name="newTag"
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand text-sm"
                    placeholder="Add tag..."
                    aria-label="Add tag"
                  />
                  <Button size="sm" variant="secondary" onClick={addTag}>
                    Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {formData.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-xs"
                    >
                      {tag}
                      <button
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            tags: prev.tags.filter((t) => t !== tag),
                          }))
                        }
                        className="text-gray-400 hover:text-gray-600"
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Author */}
          <Card>
            <CardHeader>
              <h3 className="font-medium flex items-center gap-2">
                <UserCircleIcon className="w-4 h-4" />
                Author
              </h3>
            </CardHeader>
            <CardBody className="space-y-4">
              <div>
                <label htmlFor="post-blog-author" className="block text-sm font-medium text-gray-700 mb-1">
                  Blog Author
                </label>
                <select
                  id="post-blog-author"
                  name="blogAuthor"
                  value={formData.blog_author_id}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, blog_author_id: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                  aria-label="Blog author"
                >
                  <option value="">Select author...</option>
                  {authors?.map((author) => (
                    <option key={author.id} value={author.id}>
                      {author.name}
                    </option>
                  ))}
                </select>
              </div>

              {formData.blog_author_id && authors && (
                <div className="bg-gray-50 rounded-lg p-3">
                  {(() => {
                    const author = authors.find((a) => a.id === formData.blog_author_id);
                    if (!author) return null;
                    return (
                      <div className="flex items-start gap-3">
                        {author.avatar_url ? (
                          <img
                            src={author.avatar_url}
                            alt={author.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <UserCircleIcon className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-gray-900">{author.name}</p>
                          <p className="text-xs text-gray-500 line-clamp-2">{author.bio}</p>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </CardBody>
          </Card>

          {/* Featured Image */}
          <Card>
            <CardHeader>
              <h3 className="font-medium flex items-center gap-2">
                <PhotoIcon className="w-4 h-4" />
                Featured Image
              </h3>
            </CardHeader>
            <CardBody className="space-y-4">
              {/* Image preview or placeholder */}
              {formData.featured_image_url ? (
                <div className="relative">
                  <img
                    src={formData.featured_image_url}
                    alt={formData.featured_image_alt || 'Featured'}
                    className="w-full h-40 object-cover rounded-lg"
                  />
                  <button
                    onClick={() => setFormData(prev => ({ ...prev, featured_image_url: '' }))}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    title="Remove image"
                  >
                    <XCircleIcon className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="w-full h-40 bg-gray-100 rounded-lg flex flex-col items-center justify-center text-gray-400 border-2 border-dashed border-gray-300">
                  <PhotoIcon className="w-12 h-12 mb-2" />
                  <span className="text-sm">No image yet</span>
                </div>
              )}

              {/* Generate/Regenerate button */}
              {!isNew && (
                <Button
                  variant={formData.featured_image_url ? 'secondary' : 'primary'}
                  onClick={handleRegenerateImage}
                  loading={isRegeneratingImage}
                  disabled={!formData.featured_image_suggestion?.trim()}
                  className="w-full"
                  leftIcon={isRegeneratingImage ? undefined : <SparklesIcon className="w-4 h-4" />}
                >
                  {isRegeneratingImage
                    ? 'Generating...'
                    : formData.featured_image_url
                      ? 'Regenerate Image'
                      : 'Generate Image with AI'
                  }
                </Button>
              )}

              {/* Image prompt suggestion */}
              <div>
                <label htmlFor="post-featured-image-prompt" className="block text-sm font-medium text-gray-700 mb-1">
                  Image Prompt (for AI generation)
                </label>
                <textarea
                  id="post-featured-image-prompt"
                  name="featuredImagePrompt"
                  value={formData.featured_image_suggestion}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      featured_image_suggestion: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand text-sm"
                  rows={3}
                  placeholder="Describe the image you want AI to generate..."
                  aria-label="Image prompt"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {formData.featured_image_suggestion
                    ? 'Click the button above to generate an image from this prompt.'
                    : 'Add a description and click Generate to create an AI image.'}
                </p>
              </div>

              {/* Manual URL input */}
              <div>
                <label htmlFor="post-featured-image-url" className="block text-sm font-medium text-gray-700 mb-1">
                  Or paste Image URL
                </label>
                <input
                  id="post-featured-image-url"
                  name="featuredImageUrl"
                  type="url"
                  value={formData.featured_image_url}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      featured_image_url: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand text-sm"
                  placeholder="https://..."
                  aria-label="Featured image URL"
                />
              </div>

              {/* Alt text */}
              <div>
                <label htmlFor="post-featured-image-alt" className="block text-sm font-medium text-gray-700 mb-1">
                  Alt Text
                </label>
                <input
                  id="post-featured-image-alt"
                  name="featuredImageAlt"
                  type="text"
                  value={formData.featured_image_alt}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      featured_image_alt: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand text-sm"
                  placeholder="Describe the image for accessibility..."
                  aria-label="Featured image alt text"
                />
              </div>
            </CardBody>
          </Card>

          {/* Stats (for existing posts) */}
          {post && (
            <Card>
              <CardHeader>
                <h3 className="font-medium flex items-center gap-2">
                  <ChartBarIcon className="w-4 h-4" />
                  Stats
                </h3>
              </CardHeader>
              <CardBody>
                <dl className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <dt className="text-gray-500">Views</dt>
                    <dd className="font-medium">{post.view_count.toLocaleString()}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Shares</dt>
                    <dd className="font-medium">{post.share_count.toLocaleString()}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Words</dt>
                    <dd className="font-medium">{post.word_count.toLocaleString()}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Read Time</dt>
                    <dd className="font-medium">{post.reading_time_minutes} min</dd>
                  </div>
                </dl>
              </CardBody>
            </Card>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      <Modal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        title={formData.title || 'Preview'}
        size="xl"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className="capitalize">
              {formData.category_slug.replace(/-/g, ' ') || 'Uncategorized'}
            </span>
          </div>

          {formData.featured_image_url && (
            <img
              src={formData.featured_image_url}
              alt={formData.featured_image_alt || formData.title}
              className="w-full h-48 object-cover rounded-lg"
            />
          )}

          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Meta Description</h4>
            <p className="text-sm text-gray-600">{formData.meta_description}</p>
          </div>

          <div
            className="prose prose-sm max-w-none max-h-96 overflow-y-auto"
            dangerouslySetInnerHTML={{ __html: formData.content }}
          />
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Reject Modal */}
      <Modal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Reject Blog Post"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Rejecting this post will move it back to drafts.
          </p>
          <div>
            <label htmlFor="post-rejection-reason" className="block text-sm font-medium text-gray-700 mb-1">
              Rejection Reason *
            </label>
            <textarea
              id="post-rejection-reason"
              name="rejectionReason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              rows={3}
              placeholder="Please provide feedback for the author..."
              aria-label="Rejection reason"
            />
          </div>
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => rejectMutation.mutate(rejectionReason)}
            loading={rejectMutation.isPending}
            disabled={!rejectionReason.trim()}
          >
            Reject
          </Button>
        </ModalFooter>
      </Modal>

      {/* Override Confirmation Modal */}
      <Modal
        isOpen={showOverrideConfirmModal}
        onClose={() => setShowOverrideConfirmModal(false)}
        title="Publish Without Image?"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg">
            <ExclamationTriangleIcon className="w-6 h-6 text-amber-600 flex-shrink-0" />
            <div>
              <p className="text-amber-800 font-medium">This is not recommended</p>
              <p className="text-sm text-amber-700 mt-1">
                Blog posts without images perform significantly worse for SEO and social sharing.
                This should only be used as a last resort.
              </p>
            </div>
          </div>
          <p className="text-gray-600">
            Are you sure you want to enable publishing without a featured image?
            This action will be logged in the audit trail.
          </p>
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowOverrideConfirmModal(false)}>
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={confirmOverrideEnable}
          >
            Enable Override
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
